import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useCachedQuery } from '@/hooks/useCachedQuery'
import { useInvalidar } from '@/hooks/useCachedData'
import type { Proyecto, Tarea, Cliente, Servicio, Cotizacion, EstadoProyecto } from '@/lib/types'
import { formatDate, formatCurrency } from '@/lib/utils'
import ProyectoModal from '@/components/proyectos/ProyectoModal'
import {
  IconCircleFilled,
  IconCalendar, IconDollar, IconUser, IconPlus,
} from '@/components/icons/Icons'
import { PageHeader } from '@/components/ui'

const ESTADO_BADGE: Record<EstadoProyecto, { label: string; icon: React.ReactNode; className: string }> = {
  activo: { label: 'Activo', icon: <IconCircleFilled size={8} style={{ color: 'var(--success)' }} />, className: 'badge-activo' },
  pausado: { label: 'Pausado', icon: <IconCircleFilled size={8} style={{ color: 'var(--warning)' }} />, className: 'badge-pausado' },
  completado: { label: 'Completado', icon: <IconCircleFilled size={8} style={{ color: 'var(--info)' }} />, className: 'badge-completado' },
  cancelado: { label: 'Cancelado', icon: <IconCircleFilled size={8} style={{ color: 'var(--danger)' }} />, className: 'badge-cancelado' },
}

export default function Proyectos() {
  const [search, setSearch] = useState('')
  const [filtroEstado, setFiltroEstado] = useState<EstadoProyecto | 'todos'>('todos')
  const [showModal, setShowModal] = useState(false)

  // Las cinco listas por `useCachedQuery`, una por tabla y no un `loadData`
  // propio: asi comparten cache con Empresas, Servicios y Cotizaciones, que ya
  // las traen. Entrar a Proyectos despues de Empresas no vuelve a pedir
  // clientes, y volver aca no pide nada.
  const { data: proyectos = [], isLoading: cargandoProy } = useCachedQuery<Proyecto>('proyectos')
  const { data: tareas = [], isLoading: cargandoTareas } = useCachedQuery<Tarea>('tareas')
  const { data: clientes = [] } = useCachedQuery<Cliente>('clientes')
  const { data: servicios = [] } = useCachedQuery<Servicio>('servicios')
  const { data: cotizaciones = [] } = useCachedQuery<Cotizacion>('cotizaciones')
  // El contenido de la tabla son proyectos y tareas; las otras tres son
  // diccionarios para las columnas, y esperarlas dejaria la pantalla en blanco
  // por algo que solo rellena un nombre.
  const loading = cargandoProy || cargandoTareas
  const invalidar = useInvalidar()
  // Un proyecto nuevo invalida proyectos Y tareas: el modal puede crear las
  // tareas iniciales a partir de la cotizacion.
  const loadData = () => { invalidar(['proyectos']); invalidar(['tareas']) }

  const clientesMap = useMemo(() => {
    const map: Record<number, string> = {}
    clientes.forEach(c => { map[c.id] = c.empresa })
    return map
  }, [clientes])

  const tareasPerProyecto = useMemo(() => {
    const map: Record<number, { total: number; done: number }> = {}
    tareas.forEach(t => {
      // Las tareas de oportunidad (sin proyecto) no cuentan en ningun tablero.
      if (t.proyecto_id == null) return
      if (!map[t.proyecto_id]) map[t.proyecto_id] = { total: 0, done: 0 }
      map[t.proyecto_id].total++
      if (t.estado === 'completada') map[t.proyecto_id].done++
    })
    return map
  }, [tareas])

  const filtered = useMemo(() => {
    return proyectos
      .filter(p => {
        if (filtroEstado !== 'todos' && p.estado !== filtroEstado) return false
        if (search) {
          const q = search.toLowerCase()
          const clienteName = clientesMap[p.cliente_id] ?? ''
          return (
            p.nombre.toLowerCase().includes(q) ||
            clienteName.toLowerCase().includes(q) ||
            (p.descripcion?.toLowerCase().includes(q) ?? false)
          )
        }
        return true
      })
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  }, [proyectos, filtroEstado, search, clientesMap])

  function openCreate() {
    setShowModal(true)
  }

  return (
    <div className="page-enter" id="page-proyectos">
      <PageHeader
        breadcrumbs={[{ label: 'Operaciones' }, { label: 'Proyectos' }]}
        meta={`${proyectos.length} proyectos`}
        actions={
          <button className="btn btn-primary" onClick={openCreate} id="btn-nuevo-proyecto">
            <IconPlus size={16} /> Nuevo Proyecto
          </button>
        }
      />

      {/* Filters */}
      <div className="proyectos-filters">
        <input
          className="form-input"
          id="proyectos-search"
          placeholder="Buscar por nombre, cliente..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <div className="tags-filter-bar" style={{ marginBottom: 0 }}>
          <span className="tags-filter-label">Estado:</span>
          <button
            className={`tag-chip ${filtroEstado === 'todos' ? 'active' : ''}`}
            onClick={() => setFiltroEstado('todos')}
          >
            Todos
          </button>
          {(Object.keys(ESTADO_BADGE) as EstadoProyecto[]).map(e => (
            <button
              key={e}
              className={`tag-chip ${filtroEstado === e ? 'active' : ''}`}
              onClick={() => setFiltroEstado(e)}
            >
              {ESTADO_BADGE[e].icon} {ESTADO_BADGE[e].label}
            </button>
          ))}
        </div>
      </div>

      {/* Project Grid */}
      {loading ? (
        <div className="empty-state"><p>Cargando proyectos...</p></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
          </svg>
          <h3>Sin proyectos</h3>
          <p>{search || filtroEstado !== 'todos' ? 'No se encontraron resultados' : 'Crea tu primer proyecto para empezar'}</p>
          {!search && filtroEstado === 'todos' && (
            <button className="btn btn-primary" onClick={openCreate}>+ Nuevo Proyecto</button>
          )}
        </div>
      ) : (
        <div className="proyectos-grid">
          {filtered.map(p => {
            const stats = tareasPerProyecto[p.id] || { total: 0, done: 0 }
            const progress = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0
            // Un estado fuera del catálogo no puede tumbar la pantalla entera:
            // antes, un proyecto guardado con otro valor dejaba el módulo en blanco.
            const badge = ESTADO_BADGE[p.estado] ?? {
              label: p.estado || 'Sin estado',
              icon: <IconCircleFilled size={8} style={{ color: 'var(--text-muted)' }} />,
              className: '',
            }

            return (
              <div key={p.id} className="proyecto-card" id={`proyecto-card-${p.id}`}>
                <div className="proyecto-card-header">
                  <span className={`badge ${badge.className}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {badge.icon} {badge.label}
                  </span>
                </div>

                <Link to={`/proyectos/${p.id}`} className="proyecto-card-link">
                  <h3 className="proyecto-card-title">{p.nombre}</h3>
                  <p className="proyecto-card-client" style={{ display: 'flex', alignItems: 'center', gap: 4 }}><IconUser size={13} /> {clientesMap[p.cliente_id] || 'Sin cliente'}</p>

                  {p.descripcion && (
                    <p className="proyecto-card-desc">{p.descripcion.slice(0, 100)}{p.descripcion.length > 100 ? '...' : ''}</p>
                  )}

                  {/* Progress bar */}
                  <div className="proyecto-progress">
                    <div className="proyecto-progress-bar">
                      <div className="proyecto-progress-fill" style={{ width: `${progress}%` }} />
                    </div>
                    <span className="proyecto-progress-text">
                      {stats.done}/{stats.total} tareas ({progress}%)
                    </span>
                  </div>

                  {/* Meta info */}
                  <div className="proyecto-card-meta">
                    {p.fecha_fin_estimada && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><IconCalendar size={12} /> {formatDate(p.fecha_fin_estimada)}</span>
                    )}
                    {p.presupuesto > 0 && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><IconDollar size={12} /> {formatCurrency(p.presupuesto, p.moneda)}</span>
                    )}
                  </div>
                </Link>
              </div>
            )
          })}
        </div>
      )}

      {showModal && (
        <ProyectoModal
          proyecto={null}
          clientes={clientes}
          servicios={servicios}
          cotizaciones={cotizaciones}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); loadData() }}
        />
      )}
    </div>
  )
}
